//Built-in libraries

import { createColumnHelper } from '@tanstack/react-table';
//Third party libraries
import { useLocation, useNavigate } from 'react-router';
//Custom components
import { CurrencyFormatDisplay } from '@/components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import { useApplicationsListContext } from '@/features/ApplicationForm/Components/ApplicationsList/ApplicationsListProvider';
//Services
import { convertEnumToText } from '@/utils';
import { BDCDPPFiltersForm } from './BBAFiltersForm';

//STP Libraries

import { ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState } from 'react';
import { patchArchiveApplication } from '@/api/Rover/ApplicationForm';
import Button from '@/components/UI/Button';
import Confirm from '@/components/UI/Modal/Confirm';
import ReactTable from '@/components/UI/ReactTable';
import { DATE_FORMAT } from '@/constants';
import { RoverReactQueryKey } from '@/constants/ReactQueryKeys';
//Assets and Miscellaneous
import Styles from './styles.module.css';

//Component Begin

export const BHAPPApplicationsList = () => {
  const { data, tablePagination, showFilterModal, toggleFilterModal, applyFiltersHandler } = useApplicationsListContext();

  const navigate = useNavigate();
  const location = useLocation();

  const [selectedRow, setSelectedRow] = useState<any[]>([]);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const queryClient = useQueryClient();

  const archiveApplication = useMutation(
    () => {
      return Promise.all(
        selectedRow.map((data) =>
          patchArchiveApplication({
            applicationId: data.original.id,
          })
        )
      );
    },
    {
      onSuccess: () => {
        setShowArchiveConfirm(false);
        queryClient.invalidateQueries([RoverReactQueryKey.GET_APPLICATIONS]);
      },
    }
  );

  const confirmArchiveApplication = async () => {
    archiveApplication.mutate();
  };

  const cancelArchiveApplication = () => {
    setShowArchiveConfirm(false);
  };

  // const tableColumns = [
  //   {
  //     id: "proposerName",
  //     Header: "Proposer Name",
  //     accessor: "applicationData",
  //     minWidth: 250,
  //     maxWidth: 250,
  //     Cell: (row: any) => {
  //       const applicationInformation = row.value?.applicantInfo;
  //       return (
  //         <div className="text-sm text-neutralColor-medium flex flex-col max-w-md">
  //           <span className="truncate">
  //             {applicationInformation?.entityName}
  //           </span>
  //           <span className="text-xs text-neutralColor-normal">
  //             {applicationInformation?.contactPersonEmail}
  //           </span>
  //         </div>
  //       );
  //     },
  //   },
  //   {
  //     id: "proposedProperty",
  //     Header: "Proposed Property",
  //     accessor: "applicationData",
  //     minWidth: 250,
  //     maxWidth: 250,
  //     Cell: (row: any) => {
  //       const projectDetails = row?.value?.projectDetails;
  //       return (
  //         <div className="flex flex-col gap-1">
  //           <span>{projectDetails?.streetAddress}</span>
  //           <span className="text-xs text-neutralColor-normal">
  //             {projectDetails?.city}, {projectDetails?.state}
  //           </span>
  //           <span className="text-xs text-neutralColor-normal">
  //             {projectDetails?.zipCode}
  //           </span>
  //         </div>
  //       );
  //     },
  //   },
  //   {
  //     id: "developmentCost",
  //     Header: "Development Cost",
  //     accessor: "applicationData",
  //     minWidth: 200,
  //     maxWidth: 200,
  //     Cell: (row: any) => {
  //       const projectDetails = row?.value?.projectDetails;
  //       return (
  //         <div className="flex flex-col gap-1">
  //           <CurrencyFormatDisplay
  //             value={projectDetails?.developmentCostEstimate}
  //             prefix="$"
  //           />
  //         </div>
  //       );
  //     },
  //   },
  //   {
  //     id: "constructionType",
  //     Header: "Proposed Construction Type",
  //     accessor: "applicationData.projectDetails.constructionType",
  //     minWidth: 200,
  //     maxWidth: 200,
  //   },
  //   {
  //     id: "projectType",
  //     Header: "Proposed Project Type",
  //     accessor: "applicationData.projectDetails.projectType",
  //     minWidth: 200,
  //     maxWidth: 200,
  //   },
  //   {
  //     id: "status",
  //     Header: "Status",
  //     accessor: "status",
  //     minWidth: 250,
  //     maxWidth: 250,
  //     Cell: (row: any) => {
  //       return (
  //         <div className="flex items-center w-full capitalize">
  //           <span className={`${Styles[row.value]}`}>
  //             {convertEnumToText(row.value)}
  //           </span>
  //         </div>
  //       );
  //     },
  //   },
  // ];

  const columnHelper = createColumnHelper<any>();
  const columns = [
    columnHelper.accessor('applicationData', {
      id: 'proposerName',
      header: () => 'Proposer Name',
      size: 250,
      cell: (info) => {
        const applicationInformation = info.getValue()?.applicantInfo;
        return (
          <div className='text-sm text-neutralColor-medium flex flex-col max-w-md'>
            <span className='truncate'>{applicationInformation?.entityName}</span>
            <span className='text-xs text-neutralColor-normal'>{applicationInformation?.contactPersonEmail}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor('applicationData', {
      id: 'proposedProperty',
      header: () => 'Proposed Property',
      size: 250,
      cell: (info) => {
        const projectDetails = info.getValue()?.projectDetails;
        return (
          <div className='flex flex-col gap-1'>
            <span>{projectDetails?.streetAddress}</span>
            <span className='text-xs text-neutralColor-normal'>
              {projectDetails?.city}, {projectDetails?.state}
            </span>
            <span className='text-xs text-neutralColor-normal'>{projectDetails?.zipCode}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor('applicationData', {
      id: 'developmentCost',
      header: () => 'Development Cost',
      size: 200,
      cell: (info) => {
        const projectDetails = info.getValue()?.projectDetails;
        return (
          <div className='flex flex-col gap-1'>
            <CurrencyFormatDisplay value={projectDetails?.developmentCostEstimate} prefix='$' />
          </div>
        );
      },
    }),
    columnHelper.accessor('applicationData.projectDetails.constructionType', {
      id: 'constructionType',
      header: () => 'Proposed Construction Type',
      size: 200,
      // cell: (info) => {
      //   const projectInfo = info.getValue()?.projectDetails?.constructionType;
      //   return <span>{projectInfo?.q1}</span>;
      // },
    }),
    columnHelper.accessor('applicationData.projectDetails.projectType', {
      id: 'projectType',
      header: () => 'Proposed Project Type',
      size: 250,
      // cell: (info) => {
      //   return (
      //     <Badge
      //       status={info.getValue()}
      //       text={convertEnumToText(info.getValue())}
      //     />
      //   );
      // },
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => 'Status',
      size: 250,
      cell: (info) => {
        return (
          <div className='flex items-center w-full capitalize'>
            <span className={`${Styles[info.getValue()]}`}>{convertEnumToText(info.getValue())}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      header: () => 'Submitted',
      size: 250,
      cell: (info) => {
        return <div className='flex items-center w-full capitalize'>{dayjs(info.getValue()).format(DATE_FORMAT)}</div>;
      },
    }),
  ];

  return (
    <>
      {/* <Table
        cols={tableColumns}
        tableData={data?.data}
        onRowClick={(row: any) => navigate(`${location.pathname}/${row.id}`)}
        pagination={tablePagination}
      /> */}

      {showArchiveConfirm && (
        <Confirm
          title='Archive Application'
          message='You are about to archive selected application(s). Are you sure you want to archive ?'
          acceptButtonLabel='Archive'
          declineButtonLabel='Cancel'
          accept={confirmArchiveApplication}
          decline={cancelArchiveApplication}
        />
      )}

      {selectedRow.length > 0 && (
        <div className='absolute top-4 left-28'>
          <Button
            label='Archive'
            btnStyle='neutral'
            btnSize='md'
            icon={<ArchiveBoxIcon className='h-5 w-5' />}
            onClick={() => setShowArchiveConfirm(true)}
          />
        </div>
      )}
      <ReactTable
        cols={columns}
        data={data?.data}
        onRowClick={(row: any) => navigate(`${location.pathname}/${row.id}`)}
        pagination={tablePagination}
        onRowSelect={(val: any[]) => setSelectedRow(val)}
      />

      {showFilterModal && <BDCDPPFiltersForm handleModal={toggleFilterModal} handleApplyFilter={applyFiltersHandler} />}
    </>
  );
};
